/**
 * Phase 5b — client-side permission gating.
 *
 * Fetches the caller's effective permissions from `/v1/auth/permissions/me`
 * and exposes `usePermissions()` + a helper `can(section, action)`. Admin
 * is treated as a superuser irrespective of the returned matrix.
 */
import { useQuery } from '@tanstack/react-query';
import type { PermissionAction, PermissionSection } from '@xsc/shared';
import { api } from './api-client.js';

export interface MePermissions {
  role: 'admin' | 'member' | 'viewer';
  allowed: string[];
}

export function usePermissions(): {
  data: MePermissions | null;
  isLoading: boolean;
  can: (section: PermissionSection, action: PermissionAction) => boolean;
} {
  const q = useQuery<MePermissions>({
    queryKey: ['auth', 'permissions', 'me'],
    queryFn: () => api.get('/v1/auth/permissions/me') as Promise<MePermissions>,
    staleTime: 60_000,
    retry: false,
  });
  const data = q.data ?? null;
  const can = (section: PermissionSection, action: PermissionAction): boolean => {
    if (!data) return false;
    if (data.role === 'admin') return true;
    return data.allowed.includes(`${section}:${action}`);
  };
  return { data, isLoading: q.isLoading, can };
}
